import { useContext, useEffect, useRef, useState } from 'react'
import { DialogModes, FormContext } from '../../contexts'
import './styles.scss'
import { useElementDesign } from '../useElementDesign'
import { useMultiStep } from '../../hooks/useMultiStep'
import { InlineActionbarElementWrapper } from '../../components/InlineActionbarElementWrapper'
import { validateDataTypes } from "../../utils/constants";
import { useElementStyle } from '../useElementStyle'
import SelectCountry from './selectCountry'
import { getInformation, getInformationWorkflow } from '../../api/apiDefinition'
import { useDebounceCallback } from '@react-hook/debounce'
import _ from 'lodash'
import { useInlineEditing } from '../../hooks/useInlineEditing'
import { useParams } from 'react-router'
import InlineEditing from '../../components/InlineEditing'
import { OpenElementProperties } from '../../utils/elements'

const defaultInputTextLineData = {
  type: 2,
  inputType: 1,
  property: 'Email',
  value: '',
  maxLength: 8,
  placeholder: 'you@example.com',
  layout: {
    order: 4,
  },
  styles: {
    borderWidth: 1
  },
  dependencies: {
    type: 0,
    value: 'none',
  },
  text: {
    alignment: 'left',
    fontFamily: 'arial',
    bold: false,
    italic: false,
    underline: false,
    color: 'grey'
  }
}

export default function InputTextLine({ data, ...props }) {
  const formContext = useContext(FormContext);
  const { dialogKey } = useParams();
  const { isPreviewMode, isEditMode, firstRender } = useContext(DialogModes);

  const elementDesign = useElementDesign(data.id, data);
  const multiStep = useMultiStep(data);
  const elementStyle = useElementStyle(data);
  const [countrySelect, setCountrySelect] = useState();
  data = data || defaultInputTextLineData;
  const [inputValue, setInputValue] = useState(data?.initialValue ?? '')

  const classPrefix = "inputTextLine"
  let inputType = inputTypes();
  const roundedCornerClass = data?.roundedCorners ? 'rounded-md' : ''
  const leadingTextClass = data?.leadingText ? `pl-${paddingNumber(data.leadingText)}` : ''
  const trailingTextClass = data?.trailingText ? `pr-${paddingNumber(data.trailingText)}` : ''

  const backgroundColorStyle = data?.backgroundColor ? data.backgroundColor : '#fff'
  const marginStyle = data?.margin ? data.margin : 'inherit'

  const minValue = data?.validateData === validateDataTypes.number ? data?.minValue : undefined
  const maxValue = data?.validateData === validateDataTypes.number ? data?.maxValue : undefined
  const minLength = data?.validateData === validateDataTypes.text ? data?.minLength : undefined
  const maxLength = data?.validateData === validateDataTypes.text ? data?.maxLength : undefined

  const isLabelHidden = data?.hideLabel ?? false;
  const labelClass = elementStyle.textClassName(data?.labelStyle)
  const labelStyle = elementStyle.textStyle(data?.labelStyle)
  const inputErrorClasses = elementDesign.inputErrorClasses(data.requiredField, formContext?.inputValues[data.id]?.value)

  const { borderWidth, borderColor, width: inputWidth } = elementStyle.borderAndWidthStyle();
  const { color, width: textWidth, ...textStyles } = elementStyle.textStyle(data?.text);

  const inputWrapperStyle = {
    borderWidth: !inputErrorClasses ? borderWidth : undefined,
    borderColor: !inputErrorClasses ? borderColor : undefined,
    backgroundColor: backgroundColorStyle,
    margin: marginStyle,
    color,
    width: inputWidth
  }

  const inputStyle = {
    ...textStyles,
    backgroundColor: backgroundColorStyle,
    borderWidth: 0,
    color
  }

  const isReadOnlyDependency = elementDesign.isReadOnlyDependency(data?.dependencies?.value, data?.dependencies?.type);

  const inputRef = useRef();
  const inputName = isEditMode ? 'initialValue' : '';
  const inputProps = isEditMode ? { onClick: (e) => OpenElementProperties(e, elementDesign.onClick) } : {}
  const { onChangeInlineEditing } = useInlineEditing(data)

  if (isReadOnlyDependency && inputRef.current !== undefined) {
    inputRef.current.value = '';
  }

  useEffect(() => {
    formContext?.resuablePropertyChanged(data.property, formContext?.inputValues[data.id]?.value)
    !firstRender && elementDesign.handleBlur(formContext?.inputValues[data.id]?.value, inputRef, countrySelect)
  }, []);

  useEffect(() => {
    setInputValue(data.initialValue ?? '')
  }, [data]);

  const getInformationFromApi = useDebounceCallback((value, apiRetrieveId) => {
    const execute = async () => {
      var response = await getInformation(apiRetrieveId, [{ property: data.property, value: value }])

      response.forEach(element => {
        formContext?.updateByPropertyNameValue(element.property, element.value)
      });
    }

    execute();
  },
    500
  );

  const getInformationFromWorkflow = useDebounceCallback((value, workflowRetrieveId) => {
    const execute = async () => {
      var response = await getInformationWorkflow(dialogKey, workflowRetrieveId, [{ property: data.property, value: value }])

      response.forEach(element => {
        formContext?.updateByPropertyNameValue(element.property, element.value)
      });
    }

    execute();
  },
    500);

  const onChange = async (e) => {
    let value = e.target.value;

    if (data.apiRetrieveId)
      getInformationFromApi(value, data.apiRetrieveId);

    if (data.workflowRetrieveId)
      getInformationFromWorkflow(value, data.workflowRetrieveId);

    if (!formContext)
      setInputValue(value)

    if (isEditMode)
      onChangeInlineEditing(e)

    e.preventDefault();

    switch (data.validateData) {
      case validateDataTypes.number:
        if (isPreviewMode) {
          if (value < minValue) e.target.value = minValue
          if (value > maxValue) e.target.value = maxValue
        }

        const valueWithVerification = Math.max(minValue, Math.min(maxValue, Number(value)));
        formContext?.updateValue(data.id, valueWithVerification.toString())
        break;

      default:
        formContext?.updateValue(data.id, value)
        break;
    }

    formContext?.resuablePropertyChanged(data.property, value)
  }

  function inputTypes() {
    switch (data?.validateData) {
      case validateDataTypes.text:
        return 'text';
      case validateDataTypes.number:
        return 'number';
      case validateDataTypes.email:
        return 'email';
      default:
        return 'text';
    }
  }

  function paddingNumber(text) {
    let textLength = text.length

    if (textLength < 3)
      return 8;

    if (textLength < 6)
      return 10;

    if (textLength < 8)
      return 16;

    if (textLength < 13)
      return 24;

    if (textLength < 16)
      return 28;

    if (textLength < 20)
      return 32;

    return 40;
  }

  const keyDownValidation = (e) => {

    if(e.key === 'Enter') e.preventDefault()
    
    if (data?.validateData === validateDataTypes.number) {
      let value = parseInt(e.target.value, 10);
      if (e.key === 'ArrowDown') {
        value -= 1;
        value < minValue && e.preventDefault();
      } else if (e.key === 'ArrowUp') {
        value += 1
        value > maxValue && e.preventDefault();
      }
    }
  }

  const onIncrementClick = (e) => {
    e.preventDefault();

    const value = inputRef.current.valueAsNumber

    const newValue = value + 1
    if (newValue <= maxValue) {
      (inputRef.current.value = newValue)
      formContext?.updateValue(data.id, newValue)
    }
  }

  const onSubtractClick = (e) => {
    e.preventDefault();

    const value = inputRef.current.valueAsNumber
    const newValue = value - 1
    if (newValue >= minValue) {
      (inputRef.current.value = newValue)
      formContext?.updateValue(data.id, newValue)
    }
  }

  return (
    <InlineActionbarElementWrapper designElement={elementDesign}>
      <div
        className={data?.labelTop ? `` : `sm:grid sm:grid-cols-2 sm:gap-6 sm:items-start print:grid print:grid-cols-2 print:gap-6 print:items-start`}
      >
        <div className={`${isLabelHidden ? 'hidden' : ''}`}>
          <InlineEditing
            initData={data}
            style={labelStyle}
            classes={`flex text-sm text-gray-700 sm:mt-px sm:pt-2 break-words ${labelClass}`}
            name='label'
            id={`label-${data.id}`}
          >
            <label style={{ ...labelStyle, wordBreak: 'break-word' }} className={`flex text-sm text-gray-700 sm:mt-px sm:pt-2 break-words ${labelClass}`}>
              {elementDesign.translateTerm(data.label)}
              {elementDesign.requiredFieldIndicator(data.requiredField)}
              {elementDesign.translateHelpText(data)}
              {elementDesign.validFieldIndicator()}
            </label>
          </InlineEditing>
        </div>
        <div
          style={inputWrapperStyle}
          className={`mt-2 sm:mt-0 w-full ${roundedCornerClass} ${inputErrorClasses} ${elementDesign.readOnlyElementClasses()}`}
        >
          <div className="relative flex">
            {
              data?.leadingText &&
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500 sm:text-sm">
                  {elementDesign.translateTerm(data.leadingText, 'leadingText')}
                </span>
              </div>
            }
            {
              data?.validateData === validateDataTypes.BBAN &&
              <SelectCountry inputId={data.id} setCountrySelect={setCountrySelect} countrySelect={countrySelect} inputRef={inputRef} />
            }
            <input
              ref={inputRef}
              type={inputType}
              name={inputName}
              id={data.property}
              minLength={minLength}
              maxLength={maxLength}
              value={formContext ? formContext.inputValues[data.id]?.value : inputValue}
              placeholder={elementDesign.translateTerm(data.placeholder, 'placeholder')}
              onChange={onChange}
              onKeyDown={keyDownValidation}
              style={inputStyle}
              className={`element block w-full ${leadingTextClass} ${trailingTextClass} ${roundedCornerClass} ${classPrefix} ${elementStyle.prefixClassName(classPrefix)}`}
              onBlur={() => elementDesign.handleBlur(formContext?.inputValues[data.id]?.value, inputRef, countrySelect)}
              disabled={elementDesign.isReadOnly() || multiStep.isReadOnly || isReadOnlyDependency}
              {...inputProps}
            />
            {
              data?.validateData === validateDataTypes.number &&
              <div className={"input-number-buttons"}>
                <button onClick={(e) => onIncrementClick(e)}>▲</button>
                <button onClick={(e) => onSubtractClick(e)}>▼</button>
              </div>
            }
            {
              data?.trailingText &&
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <span className="text-gray-500 sm:text-sm">
                  {elementDesign.translateTerm(data.trailingText, 'trailingText')}
                </span>
              </div>
            }
          </div>
        </div>
      </div>
    </InlineActionbarElementWrapper>
  )
}
